import { ReactNode, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { DashboardIcon, BrickIcon, EarnIcon, SwapIcon } from "@/components/icons";
import { ConnectButton } from "@/components/ConnectWallet";
import logo from "@/assets/logo_wordmark_onlight.png";
import logoGray from "@/assets/logo_wordmark_gray.png";
import logoIcon from "@/assets/logo_icon_onlight.png";
import iUSDIcon from "@/assets/tokens/icon_i-onlight.svg?url";

interface NavItemProps {
  icon: ReactNode;
  label: string;
  to: string;
  active?: boolean;
}

const NavItem = ({ icon, label, to, active }: NavItemProps) => {
  return (
    <Link
      to={to}
      className={`flex items-center h-[36px] p-[12px] mb-2 text-sm ${active ? "bg-white rounded-md shadow-sm" : "text-gray-700 hover:bg-white rounded-md"}`}
    >
      <div className={`mr-[12px] ${active ? "text-blue-600" : ""}`}>{icon}</div>
      <span>{label}</span>
    </Link>
  );
};

interface DropdownNavItemProps {
  icon: ReactNode;
  label: string;
  children: ReactNode;
  isOpen: boolean;
  toggleOpen: () => void;
}

const DropdownNavItem = ({ icon, label, children, isOpen, toggleOpen }: DropdownNavItemProps) => {
  return (
    <div className={`${isOpen ? "" : ""} mb-2`}>
      <button
        onClick={toggleOpen}
        className='h-[36px] flex items-center justify-between w-full p-[12px] text-sm text-gray-700 hover:bg-white hover:rounded-md hover:cursor-pointer'
      >
        <div className='flex items-center'>
          <div className='mr-3'>{icon}</div>
          <span>{label}</span>
        </div>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
        </svg>
      </button>
      {isOpen && <div className='ml-[16px] border-l-(--gray-b7) border-l-[2px]'>{children}</div>}
    </div>
  );
};

interface SubNavItemProps {
  label: string;
  to: string;
  active?: boolean;
}

const SubNavItem = ({ label, to, active }: SubNavItemProps) => {
  return (
    <Link
      to={to}
      className={`block px-5 py-2 text-sm text-left ${active ? "bg-white rounded-md" : "text-gray-700 hover:text-gray-900 hover:bg-white hover:rounded-md"}`}
    >
      {label}
    </Link>
  );
};

interface NavCategoryProps {
  label: string;
  children: ReactNode;
}

const NavCategory = ({ label, children }: NavCategoryProps) => {
  return (
    <div className=''>
      <h3 className='text-xs text-gray-500 uppercase tracking-wider px-[8px] mb-[16px] text-left'>{label}</h3>
      <div>{children}</div>
    </div>
  );
};

interface BestPerformerCardProps {
  tokenName: string;
  tokenIcon: string;
  apr: string;
}

const BestPerformerCard = ({ tokenName, tokenIcon, apr }: BestPerformerCardProps) => {
  return (
    <div className='bg-white rounded-lg p-4 shadow-sm'>
      <div className='text-sm text-gray-600 mb-2'>Current Best Performer</div>
      <div className='flex items-center mb-2'>
        <div className='flex items-center'>
          <img src={tokenIcon} className='w-[18px] mr-[8px]' alt={tokenName} />
          <span className='font-small'>{tokenName}</span>
        </div>
      </div>
      <div className='flex items-center mb-2'>
        <span className='font-small'>🚀 {apr} APR</span>
      </div>
      <Link to='/details' className='text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center py-1 px-2 border border-gray-300 rounded-[4px]'>
        <span>Details</span>
        <svg className='w-3.5 h-3.5 ml-1' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
        </svg>
      </Link>
    </div>
  );
};

interface PageLayoutProps {
  children: ReactNode;
}

const PageLayout = ({ children }: PageLayoutProps) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [earnDropdownOpen, setEarnDropdownOpen] = useState(false);

  return (
    <div className='flex min-h-screen bg-(--background)'>
      {/* Sidebar */}
      <div className='w-[192px] bg-(--background) flex flex-col mr-[32px]'>
        {/* Logo */}
        <div className='p-5 mb-2'>
          <div className='flex items-center'>
            <div className='flex items-center gap-[4.5px]'>
              <img src={logoIcon} alt='Logo Icon' className='h-[20px]' />
              <img src={logo} alt='Logo Wordmark' className='h-[25px]' />
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className=''>
          <div className='flex-1 mb-[24px]'>
            <NavItem
              to='/dashboard'
              label='Dashboard'
              active={currentPath === "/dashboard"}
              icon={<DashboardIcon active={currentPath === "/dashboard"} className='h-[12px]' />}
            />
            <NavItem to='/mint' label='Mint' active={currentPath === "/mint"} icon={<BrickIcon active={currentPath === "/mint"} className='h-[16px]' />} />
            <DropdownNavItem
              label='Earn'
              isOpen={earnDropdownOpen}
              toggleOpen={() => setEarnDropdownOpen(!earnDropdownOpen)}
              icon={<EarnIcon active={false} className='h-[16px]' />}
            >
              <div className='ml-[16px] flex flex-col'>
                <SubNavItem label='Bond Market' to='/bond-market' active={currentPath === "/bond-market"} />
                <SubNavItem label='oPIQ' to='/opiq' active={currentPath === "/opiq"} />
                <SubNavItem label='vPIQ' to='/vpiq' active={currentPath === "/vpiq"} />
              </div>
            </DropdownNavItem>

            <NavItem to='/swap' label='Swap' active={currentPath === "/swap"} icon={<SwapIcon active={currentPath === "/swap"} className='h-[12px]' />} />
          </div>

          <div className='mt-[24px]'>
            <NavCategory label='CONNECT'>
              <div className='flex px-[8px] space-x-4'>
                <a href='#' className='text-gray-700 hover:text-gray-900'>
                  <svg className='w-[24px]' viewBox='0 0 24 24' fill='currentColor'>
                    <path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
                  </svg>
                </a>
                <a href='#' className='text-gray-700 hover:text-gray-900'>
                  <svg className='w-[24px]' viewBox='0 0 24 24' fill='currentColor'>
                    <path d='M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.237 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z' />
                  </svg>
                </a>
              </div>
            </NavCategory>
          </div>
        </div>

        {/* Best Performer Card */}
        <div className='mt-[100px]'>
          <BestPerformerCard tokenName='iUSD Bond' tokenIcon={iUSDIcon} apr='12.45%' />
        </div>
      </div>

      {/* Main Content with Topbar */}
      <div className='flex-1 flex flex-col'>
        {/* Topbar */}
        <div className='h-16 bg-(--background) border-b border-gray-200 px-6 flex items-center justify-between'>
          <div className='flex items-center'>
            <div className='flex flex-col mr-2'>
              <div className='text-sm text-gray-600 text-left'>Current Best Performer</div>
              <div className='flex items-center gap-[15px]'>
                <div className='flex'>
                  <img src={iUSDIcon} className='w-[18px] mr-[8px]' />
                  <span className='font-medium text-sm mr-1.5'>iUSD Bond</span>
                </div>
                <div>
                  <span className='font-medium text-sm'>🚀 12.45% APR</span>
                </div>
              </div>
            </div>
            <Link
              to='/details'
              className='bg-white text-sm text-gray-600 hover:text-gray-800 flex items-center py-[9.5px] px-[11.5px] border border-gray-300 rounded-[4px]'
            >
              <span>Details</span>
              <svg className='w-3.5 h-3.5 ml-1' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
              </svg>
            </Link>
          </div>
          <div className='flex items-center'>
            <div className='flex items-center mr-4'>
              <img src={iUSDIcon} className='w-[18px] mr-[8px]' />
              <span className='text-sm'>$0.645</span>
            </div>
            <ConnectButton />
          </div>
        </div>

        {/* Page Content */}
        <div className='flex-1 pt-6 flex flex-col'>
          <div className='flex-1 bg-white rounded-tl-3xl shadow-sm overflow-hidden'>
            <div className='p-8'>{children}</div>

            {/* Footer */}
            <footer className='w-full mt-auto p-6 px-8 border-t border-gray-200'>
              <div className='mx-auto flex items-center justify-between'>
                <div className='flex items-center'>
                  <img src={logoGray} alt='Logo Icon' className='h-[20px] mr-2' />
                </div>
                <div className='flex space-x-8'>
                  <Link to='/terms' className='text-sm text-gray-500 hover:text-gray-700'>
                    Terms
                  </Link>
                  <Link to='/copyright' className='text-sm text-gray-500 hover:text-gray-700'>
                    Copyright
                  </Link>
                </div>
                <div className='flex space-x-4'>
                  <a href='#' className='text-gray-500 hover:text-gray-700'>
                    <svg className='w-[20px]' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
                    </svg>
                  </a>
                  <a href='#' className='text-gray-500 hover:text-gray-700'>
                    <svg className='w-[20px]' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.237 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z' />
                    </svg>
                  </a>
                  <a href='#' className='text-gray-500 hover:text-gray-700'>
                    <svg className='w-[20px]' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z' />
                    </svg>
                  </a>
                  <a href='#' className='text-gray-500 hover:text-gray-700'>
                    <svg className='w-[20px]' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
                    </svg>
                  </a>
                </div>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};

export { PageLayout };
